/* eslint-disable no-unused-vars */
import { useTranslate } from 'hooks/useTranslate';
import { FC, useMemo, useState } from 'react';

import Swal from 'sweetalert2';

import { removeRangeFromRanges } from 'utils/removeRangeFromRanges';
import { CoOwner, Encumbrance, Partner } from 'types';
import { FixedShare } from './types/Share';

import editEncumbrance from './utils/editEncumbrance';
import addEncumbrance from './utils/addEncumbrance';

import useEncumbranceForm from './hooks/useEncumbranceForm';

import EncumbrancesTable from './EncumbrancesTable';
import ShareEncumbranceViewForm from './components/ShareEncumbranceViewForm';
import getValidRanges from './utils/getValidRanges';
import Button from '../../Buttons';
import { EncumbranceTypes } from './constants/encumbranceOptions';

type Props = {
  share: FixedShare;
  encumbrances: Encumbrance[];
  setEncumbrances: (value: Encumbrance[]) => void;
  setEncumbrancesUpdated: (value: boolean) => void;
  partner: Partner;
  isReadOnly?: boolean;
};

const ShareEncumbranceView: FC<Props> = ({
  share,
  encumbrances,
  setEncumbrances,
  setEncumbrancesUpdated,
  partner,
  isReadOnly,
}) => {
  const { t } = useTranslate();

  const encumbranceFormResult = useEncumbranceForm({ share });

  const {
    encumbranceName,
    encumbranceNameOption,
    encumbranceDate,
    encumbranceEndDate,
    encumbranceFrom,
    encumbranceTo,
    encumbranceScriptureData,
    encumbranceFile,
    isEditing,
    editingIndex,
    validRanges,
    affectedShares,
    coOwners,
    inputLabel,
  } = encumbranceFormResult.data;

  const {
    handleChangeEncumbranceName,
    handleChangeEncumbranceDate,
    handleChangeEncumbranceEndDate,
    handleChangeEncumbranceFrom,
    handleChangeEncumbranceTo,
    handleChangeNotaryName,
    handleChangeProtocolNr,
    handleChangeNotaryCity,
    handleAddCoOwner,
    handleRemoveCoOwner,
    handleChangeEncumbranceFile,
    handleClickRange,
    handleCleanForm,
    handleCancelEdition,
    setCoOwners,
    setIsEditing,
    setValidRanges,
    setValidRangeOnInput,
    setEditValues,
  } = encumbranceFormResult.actions;

  const [showForm, setShowForm] = useState<boolean>(false);

  const handleAddEncumbrance = () => {
    if (isEditing) {
      const newEncumbrances = editEncumbrance({
        encumbrances,
        editingIndex,
        encumbranceName,
        encumbranceDate,
        encumbranceEndDate,
        encumbranceFrom,
        encumbranceTo,
        encumbranceScriptureData,
        encumbranceFile,
        coOwners,
      });
      setIsEditing(false);
      setEncumbrances(newEncumbrances);
    } else {
      const newEncumbrances = addEncumbrance({
        encumbrances,
        encumbranceName,
        encumbranceDate,
        encumbranceEndDate,
        encumbranceFrom,
        encumbranceTo,
        encumbranceScriptureData,
        encumbranceFile,
        coOwners,
      });
      setEncumbrances(newEncumbrances);
    }

    setCoOwners([]);
    const ranges = removeRangeFromRanges(
      { from: encumbranceFrom, to: encumbranceTo },
      validRanges
    );
    setValidRanges(ranges);
    setValidRangeOnInput(ranges);
    setEncumbrancesUpdated(true);
    handleCleanForm();
  };

  const mainCoOwner = useMemo(() => {
    const currentCoOwnersPercent = coOwners.reduce(
      (acc, coOwner) => acc + (coOwner?.percent || 0),
      0
    );
    const mainCoOwnerPercent = 100 - currentCoOwnersPercent;

    return {
      _id: partner?._id || '',
      name: partner?.name,
      cif: partner?.cif,
      email: partner?.email,
      percent: mainCoOwnerPercent,
      comment: t('MainPartner'),
    } as CoOwner;
  }, [partner, coOwners, t]);

  const handleEditEncumbrance = async (index: number) => {
    setShowForm(true);
    setEditValues(index, encumbrances);
  };

  const handleCancelEdit = () => {
    handleCancelEdition(encumbrances);
  };

  const removeEncumbrance = (index: number) => {
    const encumbrance = encumbrances[index];
    const ranges = getValidRanges(encumbrance, validRanges);

    setValidRanges(ranges);
    setValidRangeOnInput(ranges);

    const filteredEncumbrances = encumbrances.filter((_, i) => i !== index);
    setEncumbrances(filteredEncumbrances);
    setEncumbrancesUpdated(true);
  };

  const handleRemoveEncumbrance = (index: number) => {
    const encumbrance = encumbrances[index];
    Swal.fire({
      icon: 'warning',
      title: `<h4 class="nk-modal-title">${t(
        'AreYouSureAboutDelEncumbrance'
      )}</h4>`,
      html: `<h5 class="text-primary">${
        encumbrance?.name
      }</h5><br /><div class="caption-text">${t(
        'ThisProcedureCantUndo'
      )}</div>`,
      confirmButtonText: t('OkDelete'),
      confirmButtonColor: '#6576ff',
      allowOutsideClick: false,
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        removeEncumbrance(index);
      }
    });
  };

  const handleShowForm = () => {
    setShowForm(true);
  };

  return (
    <>
      {!showForm && !isReadOnly && (
        <div className="d-flex justify-content-end mb-4">
          <Button icon="plus" onClick={handleShowForm} outlineStyle dimStyle>
            {t('AddEncumbrance')}
          </Button>
        </div>
      )}

      {showForm && (
        <ShareEncumbranceViewForm
          share={share}
          encumbranceName={encumbranceName as EncumbranceTypes}
          encumbranceNameOption={encumbranceNameOption}
          encumbranceDate={encumbranceDate}
          encumbranceEndDate={encumbranceEndDate}
          encumbranceFrom={encumbranceFrom}
          encumbranceTo={encumbranceTo}
          affectedShares={affectedShares}
          encumbranceScriptureData={encumbranceScriptureData}
          encumbranceFile={encumbranceFile}
          mainCoOwner={mainCoOwner}
          coOwners={coOwners}
          validRanges={validRanges}
          inputLabel={inputLabel}
          isEditing={isEditing}
          handleChangeEncumbranceName={handleChangeEncumbranceName}
          handleChangeEncumbranceDate={handleChangeEncumbranceDate}
          handleChangeEncumbranceEndDate={handleChangeEncumbranceEndDate}
          handleChangeEncumbranceFrom={handleChangeEncumbranceFrom}
          handleChangeEncumbranceTo={handleChangeEncumbranceTo}
          handleAddCoOwner={handleAddCoOwner}
          handleRemoveCoOwner={handleRemoveCoOwner}
          handleAddEncumbrance={handleAddEncumbrance}
          handleCancelEdit={handleCancelEdit}
          handleClickRange={handleClickRange}
          handleChangeNotaryName={handleChangeNotaryName}
          handleChangeProtocolNr={handleChangeProtocolNr}
          handleChangeNotaryCity={handleChangeNotaryCity}
          handleChangeEncumbranceFile={handleChangeEncumbranceFile}
        />
      )}

      <EncumbrancesTable
        encumbrances={encumbrances}
        isEditing={isEditing}
        isReadOnly={isReadOnly}
        mainCoOwner={mainCoOwner}
        handleEditEncumbrance={handleEditEncumbrance}
        handleRemoveEncumbrance={handleRemoveEncumbrance}
      />
    </>
  );
};

export default ShareEncumbranceView;
